import Icon from "@/components/Icon";
import { MaterialNames } from "@/components/material_symbol_names";
import { useNavigation } from "@/hooks/useNavigation";
import classNames from "classnames";
import { Path } from "@/router";

interface NavLink {
  label: string;
  link: {
    to: Path;
    params?: Record<string, string>;
  };
  icon: string;
  domain?: string;
  /** List-return items (All People, All Activity Types) are never the current section. */
  externalLink?: boolean;
}

export interface MobileNavigationProps {
  links: NavLink[] | Record<string, NavLink[]>;
  section?: string;
  testId: string;
}

/**
 * Generic mobile navigation component that renders a list of navigation links
 * with icons, active states, and consistent styling.
 *
 * @param links - Either an array of nav links or an object with sections containing nav links
 * @param section - If links is an object, the section key to extract links from (e.g., "Manage Person")
 * @param testId - Test ID for the navigation container
 */
export default function MobileNavigation({ links, section, testId }: MobileNavigationProps) {
  const { handleNavigation, isActive } = useNavigation();

  // Extract the appropriate links array
  const navLinks = Array.isArray(links) ? links : section ? links[section] || [] : Object.values(links).flat();

  return (
    <div className="bg-sys-brand-surface" data-testid={testId}>
      <div className="pt-4 pb-20">
        <div className="space-y-1">
          {navLinks.map((link, index) => {
            const isActiveLink = link.externalLink ? false : isActive(link.link.to, link.link.params as any);

            return (
              <button
                key={index}
                onClick={() => handleNavigation(link.link.to, link.link.params as any)}
                className={classNames("w-full flex items-center gap-3 py-3 rounded-lg text-left transition-colors", {
                  "bg-sys-brand-secondary-container text-sys-brand-on-secondary-container": isActiveLink,
                  "text-sys-on-surface": !isActiveLink
                })}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon
                  name={link.icon as MaterialNames}
                  className={classNames("text-xl", {
                    "text-sys-brand-on-secondary-container": isActiveLink,
                    "text-sys-on-surface-variant": !isActiveLink
                  })}
                />
                <span className="text-body-large font-medium flex-1">{link.label}</span>
                <Icon
                  name="chevron_right"
                  className={classNames("text-xl", {
                    "text-sys-brand-on-secondary-container": isActiveLink,
                    "text-sys-on-surface-variant": !isActiveLink
                  })}
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
